<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import ReorderableTable from './ReorderableTable.vue';

import { Constants } from '@/constants';
import { useConsultationsStore, useProjectStore } from '@/stores';
import { getTranslation } from '@/composables/data-display-utils';
import type { Locales } from '@/services/types/api.types';

// STORE
const consultationsStore = useConsultationsStore();
const projectStore = useProjectStore();

const { t } = useI18n();

// PROPS
const props = defineProps<{
  itemType: number | null;
  languageEnabled: Locales[];
}>();

// EMITS
const emit = defineEmits(['row-click']);

// COMPUTED
const columns = computed(() => [
  {
    name: 'title',
    label: t('global.label.title'),
    field: (row: any) => getTranslation(row, 'title', props.languageEnabled),
    align: 'left',
  },
]);

const homepageItem = computed(() => {
  if (props.itemType === Constants.HomepageItem.Type.Consultation) {
    return {
      isLoading: consultationsStore.isLoading,
      listItems: consultationsStore.consultationListItems,
      pagination: consultationsStore.pagination,
    };
  } else if (props.itemType === Constants.HomepageItem.Type.Project) {
    return {
      isLoading: projectStore.isLoading,
      listItems: projectStore.projectListItems,
      pagination: projectStore.pagination,
    };
  }
  return {
    isLoading: false,
    listItems: [],
    pagination: {
      page: 1,
      rowsNumber: 0,
      rowsPerPage: Constants.Pagination.DefaultRowsPerPage,
    },
  };
});

// LIFECYCLE
onMounted(async () => {
  loadHomepageItems();
});

// FUNCTIONS
function handleRowClick(row: any) {
  emit('row-click', row);
}

async function loadHomepageItems() {
  if (props.itemType === Constants.HomepageItem.Type.Consultation) {
    await consultationsStore.fetchConsultations(1, 5);
  } else if (props.itemType === Constants.HomepageItem.Type.Project) {
    await projectStore.fetchAll(1, 5);
  }
}

async function fetchItems(request: any) {
  if (props.itemType === Constants.HomepageItem.Type.Consultation) {
    await consultationsStore.fetchConsultations(request.page, request.rowsPerPage, request.q);
  } else if (props.itemType === Constants.HomepageItem.Type.Project) {
    await projectStore.fetchAll(request.page, request.rowsPerPage, request.q);
  }
}

// WATCH
watch(
  () => props.itemType,
  async () => {
    loadHomepageItems();
  },
);
</script>
<template>
  <reorderable-table
    :rows="homepageItem.listItems"
    :columns="columns"
    :loading="homepageItem.isLoading"
    itemKey="id"
    @row-click="handleRowClick"
    :disable-add-button="true"
    :disable-delete-button="true"
    :disableOrderable="true"
    :pagination-enabled="true"
    :pagination="homepageItem.pagination"
    @request="fetchItems"
    :search-enabled="true"
  />
</template>
